/**
 * Hero slideshow: size the slider to the ACTIVE slide.
 *
 * The slider is a flex row, so by default every slide takes the height of the
 * tallest one. With "adapt to image" slides that differ in height (below 990px
 * the product cards stack under the image, and a slide without cards is
 * shorter) that leaves a blank band under the shorter slides. The CSS in
 * section-slideshow-custom.css keeps each slide at its natural height
 * (align-items: flex-start); this script sets the slider's height to the
 * current slide and animates it on slide change. When all slides are the same
 * height it clears the inline height and stays out of the way.
 *
 * Hooks Dawn's <slideshow-component> (assets/global.js), which dispatches
 * `slideChanged` on itself and exposes `currentPage` / `sliderItemsToShow`.
 *
 * iOS: never resize the scroll container while it is scrolling. `slideChanged`
 * fires at the half-way point of a swipe, and WebKit cancels the in-flight
 * snap/momentum scroll the moment the scroller's box changes size, so the
 * slider stalled between two slides (and autoplay, which adds one slide width
 * to the current scrollLeft, then kept landing on half-way positions). The
 * height is therefore applied only once the slider is idle: no finger on it
 * and no `scroll` event for SCROLL_IDLE_MS. Until then the taller neighbour is
 * simply clipped by the slider's overflow-y: hidden.
 */
class HeroSliderHeight {
  static instances = new WeakMap();
  static SCROLL_IDLE_MS = 150;

  static init(root = document) {
    root.querySelectorAll('slideshow-component').forEach((component) => {
      if (!component.querySelector('.hero-slide') || HeroSliderHeight.instances.has(component)) return;
      HeroSliderHeight.instances.set(component, new HeroSliderHeight(component));
    });
  }

  constructor(component) {
    this.component = component;
    this.slider = component.querySelector('.slider');
    if (!this.slider) return;

    this.sync = this.sync.bind(this);
    this.onResize = this.debounce(this.sync, 100);
    this.scrolling = false;
    this.touching = false;
    this.pendingSync = false;

    this.onSlideChanged = this.onSlideChanged.bind(this);
    component.addEventListener('slideChanged', this.onSlideChanged);
    window.addEventListener('resize', this.onResize);

    // Idle detection — see the header comment. A plain "no scroll event for
    // SCROLL_IDLE_MS" timer is used everywhere rather than `scrollend`: iOS
    // Safari lacks the event, and Chromium fires it after every programmatic
    // step, so one timer keeps the behaviour identical across browsers.
    this.onScroll = this.onScroll.bind(this);
    this.onScrollEnd = this.onScrollEnd.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);
    this.slider.addEventListener('scroll', this.onScroll, { passive: true });
    this.slider.addEventListener('touchstart', this.onTouchStart, { passive: true });
    this.slider.addEventListener('touchend', this.onTouchEnd, { passive: true });
    this.slider.addEventListener('touchcancel', this.onTouchEnd, { passive: true });

    // Heights settle once images and fonts are in.
    this.slider.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', this.sync, { once: true });
    });
    document.fonts?.ready?.then(this.sync);

    this.sync();
    requestAnimationFrame(this.sync);
  }

  get slides() {
    const shown = this.component.sliderItemsToShow;
    if (shown && shown.length) return Array.from(shown);
    return Array.from(this.slider.querySelectorAll('.slideshow__slide')).filter((s) => s.offsetWidth > 0);
  }

  get currentSlide() {
    const slides = this.slides;
    const page = Number(this.component.currentPage) || 1;
    return slides[page - 1] || slides[0];
  }

  // Dawn dispatches slideChanged from its own scroll listener, which is
  // registered before ours — so on the scroll event that crosses the midpoint
  // this runs before onScroll has flagged the slider as scrolling. Treat the
  // event as scroll activity itself and let the idle timer apply the height.
  onSlideChanged() {
    this.pendingSync = true;
    this.onScroll();
  }

  onScroll() {
    this.scrolling = true;
    clearTimeout(this.scrollIdleTimer);
    this.scrollIdleTimer = setTimeout(this.onScrollEnd, HeroSliderHeight.SCROLL_IDLE_MS);
  }

  onScrollEnd() {
    clearTimeout(this.scrollIdleTimer);
    this.scrolling = false;
    this.flush();
  }

  onTouchStart() {
    this.touching = true;
  }

  onTouchEnd() {
    this.touching = false;
    // A tap (no scroll events follow) still needs the deferred sync to run;
    // a swipe re-arms the idle timer through onScroll.
    this.flush();
  }

  get busy() {
    return this.scrolling || this.touching;
  }

  // Run a sync that was requested while the slider was busy.
  flush() {
    if (this.busy || !this.pendingSync) return;
    this.pendingSync = false;
    this.sync();
  }

  sync() {
    if (this.busy) {
      this.pendingSync = true;
      return;
    }

    const slides = this.slides;
    if (!slides.length) return;

    const heights = slides.map((s) => s.offsetHeight);
    const spread = Math.max(...heights) - Math.min(...heights);
    if (spread < 2) {
      this.slider.style.height = '';
      return;
    }

    const current = this.currentSlide;
    if (current) this.slider.style.height = `${current.offsetHeight}px`;
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

HeroSliderHeight.init();

if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (e) => HeroSliderHeight.init(e.target));
}
